"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import FlagBand from "./FlagBand";
import TrustGlobe from "./TrustGlobe";

/** Last-resort shape, used only when BOTH the server-side fetch and the client
 *  fetch fail. It is never the number a visitor normally sees: the page passes
 *  real figures in as `initialStats`, so nothing hardcoded paints first.
 *  Previously this seeded the component directly, which meant every visitor saw
 *  a stale 87 for a moment before it corrected itself. */
const FALLBACK = {
  users: 0,
  countries: 0,
  country_codes: [] as string[],
};

const POLL_MS = 60_000;

type Stats = { users: number; countries: number; country_codes: string[] };

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/** Animated count-up. Purely an entrance effect — the target always comes from
 *  the server, never from a local increment, so the number can only move
 *  because the database moved. */
function useCountUp(target: number, active: boolean) {
  const [shown, setShown] = useState(0);
  const raf = useRef(0);
  const from = useRef(0);

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(target);
      return;
    }
    const start = performance.now();
    const a = from.current;
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / 1300);
      setShown(Math.round(a + (target - a) * easeOutCubic(p)));
      if (p < 1) raf.current = requestAnimationFrame(step);
      else from.current = target;
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active]);

  return shown;
}

export default function TrustHero({ initialStats }: { initialStats?: Stats }) {
  const seed = initialStats ?? FALLBACK;
  const [stats, setStats] = useState<Stats>(seed);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [delta, setDelta] = useState(0);
  const prevUsers = useRef(seed.users);

  const load = useCallback(async (first: boolean) => {
    try {
      const res = await fetch("/api/public/stats", { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as Stats;
      if (typeof data.users !== "number" || typeof data.countries !== "number") return;
      setStats(data);
      // Only flag a delta on a later poll — the first load isn't "growth".
      if (!first && data.users > prevUsers.current) {
        setDelta(data.users - prevUsers.current);
        setTimeout(() => setDelta(0), 5000);
      }
      prevUsers.current = data.users;
    } catch {
      /* keep the fallback — a stat band is not worth breaking the hero over */
    }
  }, []);

  useEffect(() => {
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Staged entrance: the globe draws itself first, then the copy arrives.
  useEffect(() => {
    document.body.classList.add("sf-intro");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const steps: [string, number][] = reduce
      ? [["mask", 0], ["pill", 0], ["h1", 0], ["sub", 0], ["cta", 0], ["stats", 0], ["log", 0], ["flags", 0]]
      : [["mask", 1350], ["pill", 1500], ["h1", 1700], ["sub", 1950], ["cta", 2150],
         ["stats", 2350], ["log", 2550], ["flags", 2800]];
    const timers = steps.map(([key, ms]) =>
      setTimeout(() => setRevealed((s) => new Set(s).add(key)), ms)
    );
    return () => {
      timers.forEach(clearTimeout);
      document.body.classList.remove("sf-intro");
    };
  }, []);

  const rv = (key: string) => `sf-rv${revealed.has(key) ? " sf-show" : ""}`;
  const users = useCountUp(stats.users, revealed.has("stats"));
  const countries = useCountUp(stats.countries, revealed.has("stats"));

  return (
    <section className="relative overflow-hidden hero-dark min-h-[720px]">
      <TrustGlobe className="absolute inset-0 z-[1] overflow-hidden" />
      <div
        className={`absolute inset-0 z-[2] pointer-events-none sf-mapmask${
          revealed.has("mask") ? " sf-show" : ""
        }`}
        style={{
          background:
            "linear-gradient(90deg,#080d18 0%,rgba(8,13,24,.86) 19%,rgba(8,13,24,.18) 44%,transparent 64%)",
        }}
      />
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 46% 44% at 28% 44%,rgba(212,160,18,.11) 0%,transparent 64%)",
        }}
      />

      <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-6 pt-24 sm:px-10 lg:grid-cols-[minmax(0,.60fr)_minmax(0,.40fr)]">
        <div>
          <span
            className={`${rv("pill")} inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-1.5 text-xs text-white/70`}
          >
            <i className="h-1.5 w-1.5 rounded-full bg-emerald-400 sf-live-dot" />
            Publicly verifiable · {stats.countries} countries
          </span>

          <h1
            className={`${rv("h1")} font-editorial mt-6 text-[clamp(2rem,3.9vw,3.05rem)] font-bold leading-[1.05] tracking-tight text-white`}
          >
            Trusted in {stats.countries} countries.
            <br />
            <span className="text-[var(--color-gold)]">Verifiable anywhere.</span>
          </h1>

          <p className={`${rv("sub")} mt-5 max-w-[48ch] text-base leading-relaxed text-white/60 sm:text-[17px]`}>
            Every signature is recorded on a{" "}
            <b className="font-medium text-white">public, tamper-evident proof log</b> and embedded
            in the PDF — so anyone can verify it without asking us.
          </p>

          <div className={`${rv("cta")} mt-7 flex flex-wrap gap-3`}>
            <Link href="/register" className="btn-primary text-[15px]">
              Start signing free →
            </Link>
            <Link href="/verify" className="btn-hero-ghost text-[15px]">
              Verify a document
            </Link>
          </div>

          <div className={`${rv("stats")} mt-7 flex`}>
            <div className="border-r border-white/15 pr-7">
              <div className="font-display flex items-baseline gap-2 text-4xl font-extrabold leading-none tracking-tight text-white">
                {users.toLocaleString()}
                {delta > 0 && (
                  <span className="text-xs font-bold text-emerald-400">+{delta}</span>
                )}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.08em] text-white/40">
                Users &amp; organizations
              </div>
            </div>
            <div className="pl-7">
              <div className="font-display text-4xl font-extrabold leading-none tracking-tight text-white">
                {countries}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.08em] text-white/40">
                Countries
              </div>
            </div>
          </div>

          <div
            className={`${rv("log")} mt-6 inline-flex overflow-hidden rounded-[10px] border border-white/[0.12] bg-white/[0.03]`}
          >
            <div className="flex items-center gap-2 whitespace-nowrap border-r border-white/[0.09] px-4 py-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399] sf-live-dot" />
              <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-white/35">
                Live
              </span>
            </div>
            <div className="whitespace-nowrap border-r border-white/[0.09] px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-white/35">
              Public transparency log
            </div>
            <div className="whitespace-nowrap px-4 py-2.5 text-xs text-white/55">
              Every signature recorded &amp;{" "}
              <b className="font-bold text-white">independently verifiable</b>
            </div>
          </div>

        </div>

        <div aria-hidden="true" />
      </div>

      <div
        className={`${rv("flags")} relative z-10 mx-auto mt-7 max-w-6xl border-t border-white/[0.08] px-6 pb-11 pt-5 sm:px-10`}
      >
        <FlagBand codes={stats.country_codes} />
      </div>
    </section>
  );
}
